import {DragDropModule} from '@angular/cdk/drag-drop';
import {CommonModule, registerLocaleData} from '@angular/common';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import {CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, NgModule, provideAppInitializer} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatChipListbox, MatChipOption, MatChipsModule} from '@angular/material/chips';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatNativeDateModule, MatRipple, MatRippleModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {MatSelectModule} from '@angular/material/select';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {MatTooltipModule} from '@angular/material/tooltip';
import {BrowserModule} from '@angular/platform-browser';
import {provideAnimations} from '@angular/platform-browser/animations';
import {RouterModule} from '@angular/router';
import {SatPopoverModule} from '@ncstate/sat-popover';
import {FlexLayoutModule} from '@ngbracket/ngx-layout';
import {provideTranslateService, TranslateCompiler, TranslatePipe} from '@ngx-translate/core';
import {provideTranslateHttpLoader} from '@ngx-translate/http-loader';
import {ApiModule, Configuration} from '@npck/af-frontend-api';
import {AppConfigService, CommonsModule, SentryInterceptor, SentryService, TracingInterceptor, TracingService} from '@npck/libs/commons';
import {UiKitModule} from '@npck/libs/ui-kit';
import {Span} from '@opentelemetry/api';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import customParserFormat from 'dayjs/plugin/customParseFormat';
import {PdfViewerModule} from 'ng2-pdf-viewer';
import {ClipboardModule} from 'ngx-clipboard';
import {NgxDropzoneModule} from 'ngx-dropzone';
import {NgxEditorModule} from 'ngx-editor';
import {HIGHLIGHT_OPTIONS, HighlightModule} from 'ngx-highlightjs';
import {NgxMaskDirective, NgxMaskPipe, provideNgxMask} from 'ngx-mask';
import {NgxSkeletonLoaderModule} from 'ngx-skeleton-loader';
import {TranslateMessageFormatCompiler} from 'ngx-translate-messageformat-compiler';
import {provideNgxWebstorage, withLocalStorage, withSessionStorage} from 'ngx-webstorage';
import versionConfig from '../assets/config/version.json';
import {appRoutes} from './app-routing.module';
import {DatePickerFieldComponent} from './components/date-picker-field/date-picker-field.component';
import {DateRangePickerFieldComponent} from './components/date-range-picker-field/date-range-picker-field.component';
import {DeleteDialogComponent} from './components/delete-dialog/delete-dialog.component';
import {DictionarySelectorComponent} from './components/dictionary/dictionary-selector.component';
import {DropZoneControllerComponent} from './components/drop-zone/drop-zone-controller.component';
import {ChangeDetailDialogComponent} from './components/entity-history/change-detail-dialog/change-detail-dialog.component';
import {EntityHistoryComponent} from './components/entity-history/entity-history.component';
import {FieldLabelPipe} from './components/entity-history/field-label.pipe';
import {FileGroupPreviewComponent} from './components/files/file-group-preview/file-group-preview.component';
import {FilePreviewComponent} from './components/files/file-preview/file-preview.component';
import {FileComponent} from './components/files/file/file.component';
import {FilesComponent} from './components/files/files.component';
import {FilterDialogComponent} from './components/full-table/filter/filter-dialog/filter-dialog.component';
import {FilterComponent} from './components/full-table/filter/filter.component';
import {FullTableComponent} from './components/full-table/full-table.component';
import {PaginatorComponent} from './components/full-table/paging/paginator.component';
import {TableComponent} from './components/full-table/table/table.component';
import {IpInputComponent} from './components/ip-input/ip-input.component';
import {ParticipantDetailsComponent} from './components/participant-details/participant-details.component';
import {PasswordRequirementsComponent} from './components/password-requirements/password-requirements.component';
import {PhoneNumberComponent} from './components/phone-number/phone-number.component';
import {PriorityComponent} from './components/priority/priority.component';
import {RecoveryStatusComponent} from './components/recovery-status/recovery-status.component';
import {StatusComponent} from './components/status/status.component';
import {SupportNoticeComponent} from './components/support-notice/support-notice.component';
import {WarningPanelComponent} from './components/warning-panel/warning-panel.component';
import {AuthHolderService} from './domain/auth-holder.service';
import {AppErrorHandler} from './infra/app-error-handler.service';
import {DATE_FORMATS, DateTimeAdapter} from './infra/date-time.adapter';
import {DefaultMaxLengthDirective} from './infra/default-max-length.directive';
import {DigitsLatinUppercaseDirective} from './infra/digits-latin-uppercase.directive';
import {FirstLetterCapitalizedPipe} from './infra/first-letter-capitalized.pipe';
import {RefreshTokenInterceptor} from './infra/refresh-token-interceptor';
import {TextMiddleEllipsisDirective} from './infra/text-middle-ellipsis.directive';
import {MainContainerComponent} from './layout/main-container/main-container.component';
import {ChangePasswordDialogComponent} from './layout/main-layout/change-password/change-password-dialog.component';
import {MainLayoutComponent} from './layout/main-layout/main-layout.component';
import {AdminComponent} from './pages/admin/admin.component';
import {DictionaryCreateEditComponent} from './pages/admin/dictionary/dictionary-create-edit/dictionary-create-edit.component';
import {DictionaryComponent} from './pages/admin/dictionary/dictionary.component';
import {JobsComponent} from './pages/admin/jobs/jobs.component';
import {OrganizationCreateEditComponent} from './pages/admin/organizations/create-edit/organization-create-edit.component';
import {OrganizationDetailsComponent} from './pages/admin/organizations/details/organization-details.component';
import {OrganizationDocumentCreateEditComponent} from './pages/admin/organizations/documents/organization-document-create-edit.component';
import {OrganizationsComponent} from './pages/admin/organizations/organizations.component';
import {UserCreateEditComponent} from './pages/admin/users/create-edit/user-create-edit.component';
import {UserDetailsComponent} from './pages/admin/users/details/user-details.component';
import {UsersComponent} from './pages/admin/users/users.component';
import {AfCentreComponent} from './pages/af-centre/af-centre.component';
import {ActionPanelComponent} from './pages/analytics/action-pannel/action-panel.component';
import {AnalyticsComponent} from './pages/analytics/analytics.component';
import {
    ClassificationCommonInformationTabComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-common-information-tab/classification-common-information-tab.component';
import {
    ClassificationCreateEditCommonInformationComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-create-edit-common-information/classification-create-edit-common-information.component';
import {
    ClassificationDetailsComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-details.component';
import {
    ClassificationTableCellDetailsDialogComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-table/clasification-table-cell/classification-details-dialog/classification-table-cell-details-dialog.component';
import {
    ClassificationTableCellComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-table/clasification-table-cell/classification-table-cell.component';
import {
    ClassificationTableHeaderComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-table/classification-table-header/classification-table-header.component';
import {
    ClassificationTableRowLabelComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-table/classification-table-row-label/classification-table-row-label.component';
import {
    ClassificationTableComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-details/classification-table/classification-table.component';
import {
    ClassificationFraudDashboardsComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-fraud-dashboards.component';
import {
    ClassificationGroupPanelComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-matrix/classification-group-panel/classification-group-panel.component';
import {
    ClassificationPanelComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-matrix/classification-group-panel/classification-panel/classification-panel.component';
import {
    ClassificationMatrixComponent,
} from './pages/analytics/classification-fraud-dashboards/classification-matrix/classification-matrix.component';
import {DashboardFilterComponent} from './pages/analytics/incident-fraud-dashboards/dashboard-filter/dashboard-filter.component';
import {DashboardFormComponent} from './pages/analytics/incident-fraud-dashboards/dashboard-form/dashboard-form.component';
import {IncidentsFraudDashboardsComponent} from './pages/analytics/incident-fraud-dashboards/incidents-fraud-dashboards.component';
import {SupersetDashboardComponent} from './pages/analytics/superset-dashboard/superset-dashboard.component';
import {CreateEditTrendComponent} from './pages/analytics/trends/create-edit/create-edit-trend.component';
import {
    MakeConclusionOrRateTrendComponent,
} from './pages/analytics/trends/create-edit/make-conclusion-or-rate-trend/make-conclusion-or-rate-trend.component';
import {CreateTrendDialogComponent} from './pages/analytics/trends/create-edit/start-generate-trend-dialog/create-trend-dialog.component';
import {
    TrendIncidentExpansionPanelComponent,
} from './pages/analytics/trends/create-edit/trend-incidents-step/trend-incident-expansion-panel/trend-incident-expansion-panel.component';
import {TrendIncidentComponent} from './pages/analytics/trends/create-edit/trend-incidents-step/trend-incident/trend-incident.component';
import {TrendIncidentsStepComponent} from './pages/analytics/trends/create-edit/trend-incidents-step/trend-incidents-step.component';
import {TrendInformationStepComponent} from './pages/analytics/trends/create-edit/trend-information-step/trend-information-step.component';
import {
    TrendStatisticExpansionPanelComponent,
} from './pages/analytics/trends/create-edit/trend-statistics-step/trend-statistic-expansion-panel/trend-statistic-expansion-panel.component';
import {
    TrendStatisticComponent,
} from './pages/analytics/trends/create-edit/trend-statistics-step/trend-statistic/trend-statistic.component';
import {TrendStatisticsStepComponent} from './pages/analytics/trends/create-edit/trend-statistics-step/trend-statistics-step.component';
import {TrendDetailsTabComponent} from './pages/analytics/trends/details/details-tab/trend-details-tab.component';
import {TrendFeedbacksTabComponent} from './pages/analytics/trends/details/feedbacks-tab/trend-feedbacks-tab.component';
import {TrendIncidentsTabComponent} from './pages/analytics/trends/details/incidents-tab/trend-incidents-tab.component';
import {TrendStatisticTabComponent} from './pages/analytics/trends/details/statistic-tab/trend-statistic-tab.component';
import {TrendDetailsComponent} from './pages/analytics/trends/details/trend-details.component';
import {TrendsComponent} from './pages/analytics/trends/trends.component';
import {FraudReportsComponent} from './pages/analytics/fraud-reports/fraud-reports.component';
import {FraudReportDetailsComponent} from './pages/analytics/fraud-reports/details/fraud-report-details.component';
import {FraudReportDataTabComponent} from './pages/analytics/fraud-reports/details/report-data-tab/fraud-report-data-tab.component';
import {FraudReportFileTabComponent} from './pages/analytics/fraud-reports/details/report-file-tab/fraud-report-file-tab.component';
import {FraudReportSignDialogComponent} from './pages/analytics/fraud-reports/details/report-sign-dialog/fraud-report-sign-dialog.component';
import {ErrorComponent} from './pages/error/error.component';
import {AmountInformationStepComponent} from './pages/incidents/create-edit/amount-information-step/amount-information-step.component';
import {CommonInformationStepComponent} from './pages/incidents/create-edit/common-information-step/common-information-step.component';
import {
    ContactDetailsExpansionPanelComponent,
} from './pages/incidents/create-edit/contact-details-step/contact-details-expansion-panel/contact-details-expansion-panel.component';
import {
    ContactDetailsFormComponent,
} from './pages/incidents/create-edit/contact-details-step/contact-details-form/contact-details-form.component';
import {ContactDetailsStepComponent} from './pages/incidents/create-edit/contact-details-step/contact-details-step.component';
import {CreateEditIncidentComponent} from './pages/incidents/create-edit/create-edit-incident.component';
import {
    MsisdnFraudExpansionPanelComponent,
} from './pages/incidents/create-edit/msisdn-fraud-step/msisdn-fraud-expansion-panel/msisdn-fraud-expansion-panel.component';
import {MsisdnFraudFormComponent} from './pages/incidents/create-edit/msisdn-fraud-step/msisdn-fraud-form/msisdn-fraud-form.component';
import {MsisdnFraudStepComponent} from './pages/incidents/create-edit/msisdn-fraud-step/msisdn-fraud-step.component';
import {
    TransactionExpansionPanelComponent,
} from './pages/incidents/create-edit/transaction-information-step/transaction-expansion-panel/transaction-expansion-panel.component';
import {
    TransactionInformationStepComponent,
} from './pages/incidents/create-edit/transaction-information-step/transaction-information-step.component';
import {
    PaymentInformationComponent,
} from './pages/incidents/create-edit/transaction-information-step/transcation-information/payment-information/payment-information.component';
import {
    TransactionInformationComponent,
} from './pages/incidents/create-edit/transaction-information-step/transcation-information/transaction-information.component';
import {
    TransactionParticipantComponent,
} from './pages/incidents/create-edit/transaction-information-step/transcation-information/transaction-participant/transaction-participant.component';
import {
    AnalyticAgentReportDialogComponent,
} from './pages/incidents/details/analytic-agent-report-dialog/analytic-agent-report-dialog.component';
import {
    AnalyticAgentReportFeedbackDialogComponent,
} from './pages/incidents/details/analytic-agent-report-feedback-dialog/analytic-agent-report-feedback-dialog.component';
import {AnalyticAgentReportsTab} from './pages/incidents/details/analytic-agent-reports-tab/analytic-agent-reports-tab';
import {ContactCreateEditComponent} from './pages/incidents/details/contacts-tab/contact-create-edit/contact-create-edit.component';
import {ContactDetailsPageComponent} from './pages/incidents/details/contacts-tab/contact-details/contact-details.component';
import {
    ContactDetailsViewTabComponent,
} from './pages/incidents/details/contacts-tab/contact-details/contact/contact-details-tab/contact-details-view-tab.component';
import {
    ContactFilesTabComponent,
} from './pages/incidents/details/contacts-tab/contact-details/contact/files-tab/contact-files-tab.component';
import {IncidentContactComponent} from './pages/incidents/details/contacts-tab/contact-details/contact/incident-contact.component';
import {
    ContactMsisdnInfoTabComponent,
} from './pages/incidents/details/contacts-tab/contact-details/contact/msisdn-info-tab/contact-msisdn-info-tab.component';
import {
    ContactStatusHistoryTabComponent,
} from './pages/incidents/details/contacts-tab/contact-details/contact/status-history-tab/contact-status-history-tab.component';
import {ContactsTabComponent} from './pages/incidents/details/contacts-tab/contacts-tab.component';
import {IncidentDetailsTabComponent} from './pages/incidents/details/details-tab/incident-details-tab.component';
import {DocumentsPanelComponent} from './pages/incidents/details/documents-tab/documents-panel/documents-panel.component';
import {DocumentsTabComponent} from './pages/incidents/details/documents-tab/documents-tab.component';
import {IncidentDetailsComponent} from './pages/incidents/details/incident-details.component';
import {InfoComponent} from './pages/incidents/details/info/info.component';
import {IncidentInquiriesTabComponent} from './pages/incidents/details/inquiries-tab/incident-inquiries-tab.component';
import {
    InquiryResponsePanelComponent,
} from './pages/incidents/details/inquiries-tab/inquiry-response-panel/inquiry-response-panel.component';
import {MsisdnFraudEntriesTabComponent} from './pages/incidents/details/msisdn-fraud-entries-tab/msisdn-fraud-entries-tab.component';
import {
    MsisdnFraudEntryCreateEditComponent,
} from './pages/incidents/details/msisdn-fraud-entries-tab/msisdn-fraud-entry-create-edit/msisdn-fraud-entry-create-edit.component';
import {RefundsTabComponent} from './pages/incidents/details/refunds-tab/refunds-tab.component';
import {ResolutionCreateComponent} from './pages/incidents/details/resolution-create/resolution-create.component';
import {IncidentResolutionTabComponent} from './pages/incidents/details/resolution-tab/incident-resolution-tab.component';
import {
    TransactionCreateEditComponent,
} from './pages/incidents/details/transactions-tab/transaction-create-edit/transaction-create-edit.component';
import {TransactionDetailsComponent} from './pages/incidents/details/transactions-tab/transaction-details/transaction-details.component';
import {
    IncidentTransactionCreditInfoTabComponent,
} from './pages/incidents/details/transactions-tab/transaction-details/transaction/credit-info-tab/incident-transaction-credit-info-tab.component';
import {
    IncidentTransactionDroppersTabComponent,
} from './pages/incidents/details/transactions-tab/transaction-details/transaction/droppers-tab/incident-transaction-droppers-tab.component';
// eslint-disable-next-line max-len
import {
    IncidentTransactionComponent,
} from './pages/incidents/details/transactions-tab/transaction-details/transaction/incident-transaction.component';
import {
    IncidentTransactionPaymentInfoTabComponent,
} from './pages/incidents/details/transactions-tab/transaction-details/transaction/payment-info-tab/incident-transaction-payment-info-tab.component';
import {
    IncidentTransactionDetailsTabComponent,
} from './pages/incidents/details/transactions-tab/transaction-details/transaction/transaction-details-tab/incident-transaction-details-tab.component';
import {TransactionsTabComponent} from './pages/incidents/details/transactions-tab/transactions-tab.component';
import {IncidentsComponent} from './pages/incidents/incidents.component';
import {CreateEditFaqComponent} from './pages/knowledge-base/faq-tab/create-edit-faq/create-edit-faq.component';
import {FaqAdminComponent} from './pages/knowledge-base/faq-tab/faq-admin/faq-admin.component';
import {FaqDetailsComponent} from './pages/knowledge-base/faq-tab/faq-admin/faq-details/faq-details.component';
import {FaqTabComponent} from './pages/knowledge-base/faq-tab/faq-tab.component';
import {FaqAnswersComponent} from './pages/knowledge-base/knowledge-base-categories/faq-answers/faq-answers.component';
import {KnowledgeBaseCategoriesComponent} from './pages/knowledge-base/knowledge-base-categories/knowledge-base-categories.component';
import {
    KnowledgeDocumentItemsComponent,
} from './pages/knowledge-base/knowledge-base-categories/knowledge-document-items/knowledge-document-items.component';
import {
    KnowledgeBaseDocumentViewComponent,
} from './pages/knowledge-base/knowledge-base-document-view/knowledge-base-document-view.component';
import {
    KnowledgeBaseArchiveComponent,
} from './pages/knowledge-base/knowledge-base-documents-tab/documents-admin/archive/knowledge-base-archive.component';
import {
    CreateEditKnowledgeBaseDocumentComponent,
} from './pages/knowledge-base/knowledge-base-documents-tab/documents-admin/create-edit/create-edit-knowledge-base-document.component';
import {
    KnowledgeBaseDocumentsTabComponent,
} from './pages/knowledge-base/knowledge-base-documents-tab/knowledge-base-documents-tab.component';
import {KnowledgeBaseComponent} from './pages/knowledge-base/knowledge-base.component';
import {LoginComponent} from './pages/login/login.component';
import {PasswordRecoveryComponent} from './pages/login/recovery/password-recovery.component';
import {NotificationItemComponent} from './pages/notifications/notification/notification-item/notification-item.component';
import {NotificationComponent} from './pages/notifications/notification/notification.component';
import {NotificationsComponent} from './pages/notifications/notifications.component';
import {OauthCallbackComponent} from './pages/oauth-callback/oauth-callback.component';
import {RefundApprovalTabComponent} from './pages/refunds/refund-details/refund-approval-tab/refund-approval-tab.component';
import {RefundDetailsTabComponent} from './pages/refunds/refund-details/refund-details-tab/refund-details-tab.component';
import {RefundDetailsComponent} from './pages/refunds/refund-details/refund-details.component';
import {RefundExecutionDialogComponent} from './pages/refunds/refund-details/refund-execution-dialog/refund-execution-dialog.component';
import {RefundRejectDialogComponent} from './pages/refunds/refund-details/refund-reject-dialog/refund-reject-dialog.component';
import {RefundRejectPanelComponent} from './pages/refunds/refund-details/refund-reject-panel/refund-reject-panel.component';
import {
    RefundCommonInformationStepComponent,
} from './pages/refunds/refunds-create-edit/refund-common-information-step/refund-common-information-step.component';
import {
    RefundTransactionsDialogComponent,
} from './pages/refunds/refunds-create-edit/refund-common-information-step/refund-transactions-dialog/refund-transactions-dialog.component';
import {
    RefundPreviewResolutionDialogComponent,
} from './pages/refunds/refunds-create-edit/refund-preview-resolution/refund-preview-resolution-dialog.component';
import {
    RefundSendToExecutionConfirmationDialogComponent,
} from './pages/refunds/refunds-create-edit/refund-send-to-execution-confirmation-dialog/refund-send-to-execution-confirmation-dialog.component';
import {
    RefundTransactionStepComponent,
} from './pages/refunds/refunds-create-edit/refund-transaction-step/refund-transaction-step.component';
import {RefundsCreateEditComponent} from './pages/refunds/refunds-create-edit/refunds-create-edit.component';
import {RefundsComponent} from './pages/refunds/refunds.component';
import {ResetPasswordComponent} from './pages/reset-password/reset-password.component';
import {CreateEditSubjectComponent} from './pages/subjects/create-edit/create-edit-subject.component';
import {SubjectCategoriesTabComponent} from './pages/subjects/details/categories-tab/subject-categories-tab.component';
import {SubjectContactsTabComponent} from './pages/subjects/details/contacts-tab/subject-contacts-tab.component';
import {
    DeleteSubjectCategoryDialogComponent,
} from './pages/subjects/details/delete-subject-category-dialog/delete-subject-category-dialog.component';
import {ResolutionInfoDialogComponent} from './pages/subjects/details/resolution-info-dialog/resolution-info-dialog.component';
import {SubjectDetailsComponent} from './pages/subjects/details/subject-details.component';
import {ExportSubjectFileDialogComponent} from './pages/subjects/export-subject-file-dialog/export-subject-file-dialog.component';
import {ImportSubjectFileDialogComponent} from './pages/subjects/import-subject-file-dialog/import-subject-file-dialog.component';
import {MsisdnDetailsComponent} from './pages/subjects/msisdn-details/msisdn-details.component';
import {SubjectsComponent} from './pages/subjects/subjects.component';
import {AppComponent} from './root/app.component';

dayjs.extend(customParserFormat);
dayjs.locale('ru');
registerLocaleData(localeRu, 'ru');

const apiConfigurationFactory = (authHolderService: AuthHolderService): Configuration => new Configuration({
    basePath: '',
    credentials: {
        AuthToken: () => authHolderService.authResponse.access_token,
        RefreshToken: () => authHolderService.authResponse.refresh_token,
    },
});

export const httpInterceptorProviders = [
    {provide: HTTP_INTERCEPTORS, useClass: TracingInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SentryInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: RefreshTokenInterceptor, multi: true},
];

export function initApp(
    appConfigService: AppConfigService,
    authHolderService: AuthHolderService,
    tracingService: TracingService,
    sentryService: SentryService,
): () => Promise<any> {
    return () => {
        return appConfigService.init({...versionConfig, enableBackendConfig: true})
            .then(() => {
                return tracingService.init((span: Span) => {
                    span.setAttribute('userId', authHolderService.userId);
                    span.setAttribute('clientTime', new Date().toString());
                });
            })
            .then(() => {
                const tagsPreview = {
                    tokenId: authHolderService.username,
                    openTelemetryTraceId: tracingService.getTraceId(),
                    clientTime: new Date().toString(),
                };
                return sentryService.init({
                    tagsFactory: () => tagsPreview,
                    groupsByTags: {
                        network: [
                            [{httpStatusText: 'Unknown Error'}, {httpStatus: '0'}],
                        ],
                    },
                });
            });
    };

}

@NgModule({
    declarations: [
        AppComponent,
        LoginComponent,
        PasswordRecoveryComponent,
        ResetPasswordComponent,
        RecoveryStatusComponent,
        MainLayoutComponent,
        MainContainerComponent,
        FilterComponent,
        FilterDialogComponent,
        TableComponent,
        FullTableComponent,
        StatusComponent,
        PaginatorComponent,
        CreateEditSubjectComponent,
        FirstLetterCapitalizedPipe,
        ImportSubjectFileDialogComponent,
        ExportSubjectFileDialogComponent,
        SubjectDetailsComponent,
        SubjectCategoriesTabComponent,
        SubjectContactsTabComponent,
        AdminComponent,
        DictionaryComponent,
        JobsComponent,
        OrganizationsComponent,
        OrganizationCreateEditComponent,
        OrganizationDetailsComponent,
        OrganizationDocumentCreateEditComponent,
        UsersComponent,
        UserCreateEditComponent,
        UserDetailsComponent,
        IncidentsComponent,
        IncidentDetailsComponent,
        InfoComponent,
        IncidentDetailsTabComponent,
        IncidentTransactionComponent,
        IncidentTransactionDetailsTabComponent,
        IncidentTransactionCreditInfoTabComponent,
        IncidentTransactionPaymentInfoTabComponent,
        IncidentTransactionDroppersTabComponent,
        EntityHistoryComponent,
        ChangeDetailDialogComponent,
        FieldLabelPipe,
        CreateEditIncidentComponent,
        CommonInformationStepComponent,
        ContactDetailsStepComponent,
        ContactDetailsExpansionPanelComponent,
        ContactDetailsFormComponent,
        AmountInformationStepComponent,
        TransactionInformationStepComponent,
        TransactionInformationComponent,
        PaymentInformationComponent,
        IpInputComponent,
        TransactionParticipantComponent,
        DeleteDialogComponent,
        DeleteSubjectCategoryDialogComponent,
        IncidentsFraudDashboardsComponent,
        DashboardFilterComponent,
        DashboardFormComponent,
        DateRangePickerFieldComponent,
        ResolutionCreateComponent,
        PasswordRequirementsComponent,
        PhoneNumberComponent,
        DictionarySelectorComponent,
        IncidentResolutionTabComponent,
        ErrorComponent,
        IncidentInquiriesTabComponent,
        InquiryResponsePanelComponent,
        DigitsLatinUppercaseDirective,
        PriorityComponent,
        FilesComponent,
        FileComponent,
        DropZoneControllerComponent,
        DocumentsTabComponent,
        FilePreviewComponent,
        FileGroupPreviewComponent,
        DocumentsPanelComponent,
        TextMiddleEllipsisDirective,
        SubjectsComponent,
        AfCentreComponent,
        MsisdnDetailsComponent,
        NotificationsComponent,
        NotificationComponent,
        NotificationItemComponent,
        ResolutionInfoDialogComponent,
        ChangePasswordDialogComponent,
        DictionaryCreateEditComponent,
        TransactionExpansionPanelComponent,
        MsisdnFraudStepComponent,
        MsisdnFraudExpansionPanelComponent,
        MsisdnFraudFormComponent,
        DatePickerFieldComponent,
        RefundsComponent,
        RefundsTabComponent,
        RefundsCreateEditComponent,
        RefundCommonInformationStepComponent,
        RefundTransactionsDialogComponent,
        RefundTransactionStepComponent,
        RefundSendToExecutionConfirmationDialogComponent,
        RefundPreviewResolutionDialogComponent,
        AnalyticAgentReportFeedbackDialogComponent,
        AnalyticAgentReportsTab,
        OauthCallbackComponent,
        RefundDetailsComponent,
        RefundApprovalTabComponent,
        RefundDetailsTabComponent,
        ParticipantDetailsComponent,
        RefundRejectDialogComponent,
        RefundExecutionDialogComponent,
        RefundRejectPanelComponent,
        WarningPanelComponent,
        SupportNoticeComponent,
        ActionPanelComponent,
        AnalyticsComponent,
        ClassificationFraudDashboardsComponent,
        ClassificationGroupPanelComponent,
        ClassificationPanelComponent,
        ClassificationDetailsComponent,
        ClassificationCommonInformationTabComponent,
        ClassificationCreateEditCommonInformationComponent,
        ClassificationTableComponent,
        ClassificationTableCellComponent,
        ClassificationTableHeaderComponent,
        ClassificationTableRowLabelComponent,
        ClassificationTableCellDetailsDialogComponent,
        ClassificationMatrixComponent,
        ContactsTabComponent,
        MsisdnFraudEntriesTabComponent,
        MsisdnFraudEntryCreateEditComponent,
        ContactCreateEditComponent,
        ContactDetailsPageComponent,
        IncidentContactComponent,
        ContactDetailsViewTabComponent,
        ContactFilesTabComponent,
        ContactMsisdnInfoTabComponent,
        ContactStatusHistoryTabComponent,
        TransactionsTabComponent,
        TransactionDetailsComponent,
        TransactionCreateEditComponent,
        FraudReportsComponent,
        FraudReportDetailsComponent,
        FraudReportDataTabComponent,
        FraudReportFileTabComponent,
        FraudReportSignDialogComponent,
        TrendsComponent,
        TrendDetailsComponent,
        TrendDetailsTabComponent,
        TrendIncidentsTabComponent,
        TrendFeedbacksTabComponent,
        TrendStatisticTabComponent,
        TrendInformationStepComponent,
        TrendIncidentsStepComponent,
        TrendIncidentExpansionPanelComponent,
        TrendIncidentComponent,
        TrendStatisticsStepComponent,
        TrendStatisticExpansionPanelComponent,
        TrendStatisticComponent,
        CreateEditTrendComponent,
        CreateTrendDialogComponent,
        MakeConclusionOrRateTrendComponent,
        FaqAdminComponent,
        KnowledgeBaseComponent,
        CreateEditFaqComponent,
        FaqTabComponent,
        KnowledgeBaseCategoriesComponent,
        FaqAnswersComponent,
        FaqDetailsComponent,
        KnowledgeDocumentItemsComponent,
        KnowledgeBaseDocumentsTabComponent,
        CreateEditKnowledgeBaseDocumentComponent,
        KnowledgeBaseArchiveComponent,
        KnowledgeBaseDocumentViewComponent,
        AnalyticAgentReportDialogComponent,
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        CommonModule,
        CommonsModule,
        BrowserModule,
        RouterModule.forRoot(appRoutes),
        SupersetDashboardComponent,
        ApiModule,
        ClipboardModule,
        DragDropModule,
        FormsModule,
        FlexLayoutModule,
        HighlightModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatChipListbox,
        MatChipOption,
        MatChipsModule,
        MatDatepickerModule,
        MatDialogModule,
        MatExpansionModule,
        MatInputModule,
        MatIconModule,
        MatNativeDateModule,
        MatProgressBarModule,
        MatProgressSpinnerModule,
        MatRadioModule,
        MatRipple,
        MatRippleModule,
        MatSelectModule,
        MatSidenavModule,
        MatTableModule,
        MatTooltipModule,
        MatTabsModule,
        NgxDropzoneModule,
        NgxMaskDirective,
        NgxMaskPipe,
        ReactiveFormsModule,
        SatPopoverModule,
        DefaultMaxLengthDirective,
        UiKitModule,
        MatCheckbox,
        TranslatePipe,
        PdfViewerModule,
        NgxEditorModule,
        NgxSkeletonLoaderModule.forRoot(),
    ],
    providers: [
        provideAnimations(),
        provideAppInitializer(() =>
            initApp(
                inject(AppConfigService),
                inject(AuthHolderService),
                inject(TracingService),
                inject(SentryService),
            )(),
        ),
        provideHttpClient(withInterceptorsFromDi()),
        provideNgxMask(),
        provideNgxWebstorage(
            withLocalStorage(),
            withSessionStorage(),
        ),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json',
            }),
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler,
            },
        }),
        {provide: Configuration, useFactory: apiConfigurationFactory, deps: [AuthHolderService]},
        {provide: ErrorHandler, useClass: AppErrorHandler},
        {provide: DateAdapter, useClass: DateTimeAdapter, deps: [MAT_DATE_LOCALE]},
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS},
        {provide: MAT_DATE_LOCALE, useValue: 'ru'},
        {
            provide: HIGHLIGHT_OPTIONS,
            useValue: {
                coreLibraryLoader: () => import('highlight.js/lib/core'),
                languages: {
                    json: () => import('highlight.js/lib/languages/json'),
                },
            },
        },
        httpInterceptorProviders,
    ],
})
export class AppModule {
}
